(ns dashboard.entity.analysis.answers
  "The principle contract of the dashboard.entity component namespaces are this:
   1. All components will NOT depend on keyword references to subs and events in
      external NSes, but rather receive data as arguments, functions and callbacks.
   2. All internal reliance on the re-frame app DB must be internally contained.
   3. All re-frame app subs and events must NOT be directly referenced by outside
      NSes, but rather via GETTER and SETTER functions - the same is true of internal
      calls - prefer using parameterized functions.
   4. The entity is a UI component that may encapsulate low-level business rules on
      UI controls, but is not responsible for hydrating components with data or entrenched
      references to component namespaces. The UI is customizable, and top-level entities
      should have configurable and interchangable UI elements."
  (:require ["react" :as react]
            [clojure.string :as str]
            [dashboard.html :as html]
            [dashboard.entity.analysis.profile :as analysis.profile]
            [dashboard.entity.question.profile :as question.profile]
            [reitit.frontend.easy :as rfe]))

(defn AssociatedAnswerLineItem
  [this-version-id
   all-versions
   on-view-version
   on-click-answer
   {:keys [created state version answer-id version-id]
    {:keys [response author additional-information]} :answer-data
    :as answer}]
  [:div.analysis-associated-answer
   {:key answer-id
    :on-click #(on-click-answer answer)}
   [:div.analysis-associated-answer-line-item
    [:div.analysis-associated-answer-line-item-content
     [:div.analysis-associated-answer-line-item-version
      (if (= version-id this-version-id)
        [:span
         (str "Associated to this version (v" version ")")
         [:span.ml-2 [analysis.profile/state-tag state]]]
        [:button.button
         {:title (str "View version " version)
          :on-click (fn [e]
                      (.preventDefault e)
                      (->> all-versions
                           (filter (comp (partial = version-id)
                                         :version-id))
                           first
                           on-view-version))}
         "Associated to  v" version
         [:span.ml-2 [analysis.profile/state-tag state]]])]
     [:div.analysis-associated-answer-line-item-response
      "A: " (html/as-hiccup-with-anchors response)]]]
   (when-not (str/blank? response) [:hr.my-1])
   [:div (html/as-hiccup-with-anchors additional-information)]
   [:hr.my-1]
   [:div.level.my-0
    [:div.analysis-associated-answer-line-item-subtext (str created)]
    [:div.analysis-associated-answer-line-item-subtext (str author)]]])

(defn QuestionnaireQuestions
  [this-version-id all-versions show-all-versions on-view-version on-click-answer answers]
  (let [{:keys [question-content
                question-display-name]} (first answers)]
    [:div.analysis-list-subsection.p-2.my-0.analysis-list-question
     [question.profile/QuestionAnswerHistory
      identity
      {:content question-content
       :display-name question-display-name
       :answers answers
       :answer-content (->> (cond->> (->> answers
                                          (sort-by (juxt :version :created))
                                          reverse)
                              (not show-all-versions)
                              (take 1))

                            (mapv (partial AssociatedAnswerLineItem
                                           this-version-id
                                           all-versions
                                           on-view-version
                                           on-click-answer))
                            (into [:div.analysis-questionnaire-question-answers]))}]]))

(defn AssociatedQuestionnaire
  [this-version-id all-versions show-all-versions on-view-version on-click-answer answers]
  (let [{:keys [questionnaire-custom-name
                questionnaire-filename
                questionnaire-id]} (first answers)]
    [:div.analysis-questionnaire
     [:div.level-left
      [:span.ml-3 "Questionnaire "]
      [:a.is-size-5.ml-3
       {:href (rfe/href :route.view/questionnaire-question-list
                        {:questionnaire-id questionnaire-id})}
       (or questionnaire-custom-name
           questionnaire-filename)]]
     (->> answers
          (sort-by :created)
          reverse
          (group-by :question-id)
          (mapv (comp (partial QuestionnaireQuestions
                               this-version-id
                               all-versions
                               show-all-versions
                               on-view-version
                               on-click-answer)
                      val))
          (into [:<>]))]))

(defn AssociatedAnswers
  [{this-version-id :version-id} all-versions on-view-version linked-answers on-click-answer question]
  (let [[show-all-versions set-show-all-versions] (react/useState false)
        [show-only-approved set-show-only-approved] (react/useState false)
        {:keys [answers loading]} linked-answers
        visible-answers (cond->> answers
                          show-only-approved
                          (filter (comp (partial = "approved")
                                        :status))
                          (boolean question)
                          (remove (comp (partial = (or (:question-id question)
                                                       (:id question)))
                                        :id))
                          (boolean question)
                          (remove (comp (partial = (or (:question-id question)
                                                       (:id question)))
                                        :question-id)))]
    [analysis.profile/field
     [analysis.profile/expandable-row-header
      (str "Associated answers"
           (when-not loading
             (str " (" (count visible-answers) ") "))
           (when (and question (= 0 (count visible-answers)))
            "Only 1 Answer - for the current Question"))
      [:button.button
       {:on-click #(set-show-all-versions not)
        :title "Show or hide the hierarchy of applied analysis"}
       (if show-all-versions
         [:i.icon.fas.fa-caret-up]
         [:i.icon.fas.fa-caret-down])]]

     [:<>
      [:input.mr-2
       {:type :checkbox
        :checked show-only-approved
        :on-change (fn [] (set-show-only-approved not))}]
      "Show Only Approved Answers"]

     (if loading
       [:span.loader]
       (if (empty? answers)
         [:div "<< No associated answers >>"]
         (->> visible-answers
              (group-by :questionnaire-id)
              (mapv (comp (partial AssociatedQuestionnaire
                                   this-version-id
                                   all-versions
                                   show-all-versions
                                   on-view-version
                                   on-click-answer)
                          val))
              (into [:div]))))]))
